import { useState, useEffect, useMemo, useRef, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useRouteMatch } from "react-router";
import { useLocalStorage } from "@rehooks/local-storage";

import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { Theme } from "@mui/material/styles";

import SearchField from "./SearchField";
import { StyledSwitch } from "../Base";
import { parseDateString } from "../../services/formatters";

const DEFAULT_ROWS_PER_PAGE = 100;

const SORT_DIRECTION = {
  ASC: -1,
  DESC: 1,
};

const StyledTableHead = styled(TableHead)(({ theme }: { theme: Theme }) => ({
  backgroundColor: theme.palette.background.default,
}));

export interface TableHeader {
  title: string;
  sort?: string;
  sortType?: "string" | "date" | "number";
  style?: React.CSSProperties;
}

interface TableComponentProps {
  TopComponent?: React.ReactElement;
  renderChildren: (item: any, index: number) => React.ReactElement;
  header?: TableHeader[];
  pagination?: boolean;
  search?: string[];
  filter?: { filterBy: string; controlLabel: string };
  pageSize?: number;
  items: Record<string, any>[];
  users?: boolean;
  WrapperComponent?: React.ComponentType;
  containerStyle?: React.CSSProperties;
  tableId?: string;
  tableStyle?: React.CSSProperties;
  defaultSort?: {
    sortBy: string;
    sortDirection: "asc" | "desc";
  };
}

const TableComponent: React.FC<TableComponentProps> = ({
  TopComponent,
  renderChildren,
  header,
  pagination = false,
  search = null,
  filter = null,
  pageSize: defaultPageSize = DEFAULT_ROWS_PER_PAGE,
  items,
  users,
  WrapperComponent = null,
  containerStyle,
  tableId = "",
  tableStyle,
  defaultSort,
}) => {
  const { t } = useTranslation();
  const routePath = useRouteMatch().path;

  // Memoize storage keys to keep them stable
  const storageKeys = useMemo(() => {
    const storageKey = "table." + routePath + (tableId ? "." + tableId : "");
    return {
      storageKey,
      navigationFlagKey: storageKey + ".shouldRestore",
      forwardNavKey: storageKey + ".forwardNav",
    };
  }, [routePath, tableId]);

  // Check if we should restore state (coming back via back button after forward navigation)
  const shouldRestoreState = useMemo(() => {
    const wasForwardNav = sessionStorage.getItem(storageKeys.forwardNavKey) === "true";
    const hasRestoreFlag = sessionStorage.getItem(storageKeys.navigationFlagKey) === "true";
    return hasRestoreFlag && wasForwardNav;
  }, [storageKeys]);

  const [preferences, setPreferences] = useLocalStorage(storageKeys.storageKey, {
    pageSize: defaultPageSize,
    filter: filter ? true : false,
    page: 0,
    searchTerm: "",
  });

  // Ensure items is always an array to prevent undefined errors
  const safeItems = items || [];
  const rowsPerPage = pagination ? preferences.pageSize : safeItems.length;
  const filterOn = preferences.filter ?? true;

  // Use ref to track if initial setup is done
  const isInitialMount = useRef(true);

  const [page, setPage] = useState(shouldRestoreState ? preferences.page || 0 : 0);
  const [searchTerm, setSearch] = useState(shouldRestoreState ? preferences.searchTerm || "" : "");
  const [sort, setSort] = useState<string>(defaultSort?.sortBy || "");
  const [sortDirection, setSortDirection] = useState(
    defaultSort?.sortDirection === "desc" ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC,
  );

  const setRowsPerPage = (pageSize: number) => {
    setPreferences({ ...preferences, pageSize });
  };

  // Clear the restoration flags after checking them, and reset state if not restoring
  useEffect(() => {
    if (isInitialMount.current) {
      sessionStorage.removeItem(storageKeys.navigationFlagKey);
      sessionStorage.removeItem(storageKeys.forwardNavKey);

      // If we didn't restore state, reset the stored values to defaults
      if (!shouldRestoreState && (preferences.page !== 0 || preferences.searchTerm !== "")) {
        setPreferences({ ...preferences, page: 0, searchTerm: "" });
      }

      isInitialMount.current = false;
    }
  }, [storageKeys, shouldRestoreState, preferences, setPreferences]);

  // Set flag when unmounting so state will be restored on next mount (if user navigates back)
  useEffect(() => {
    return () => {
      sessionStorage.setItem(storageKeys.navigationFlagKey, "true");
    };
  }, [storageKeys]);

  const sortBy = (key: string) => {
    if (sort === key) {
      setSortDirection(-sortDirection);
    } else {
      setSort(key);
    }
  };

  const sortFn = (a: any, b: any) => {
    const v1 = a[sort];
    const v2 = b[sort];

    const currentHeader = header?.find((h) => h.sort === sort);
    const sortType = currentHeader?.sortType || "string";

    if (sortType === "date") {
      // Parse dates - handle multiple formats including YYYY-MM-DD HH:mm:ss and DD.MM.YYYY
      const parseDate = (dateStr: string) => {
        if (!dateStr) return new Date(0);

        // Try using the existing parseDateString for DD.MM.YYYY format
        if (dateStr.includes(".")) {
          return parseDateString(dateStr);
        }

        // For YYYY-MM-DD or YYYY-MM-DD HH:mm:ss formats, use native Date parsing
        // which handles both formats correctly
        const parsed = new Date(dateStr);
        return isNaN(parsed.getTime()) ? new Date(0) : parsed;
      };

      const date1 = parseDate(v1);
      const date2 = parseDate(v2);
      return date1 > date2 ? -sortDirection : sortDirection;
    }

    if (sortType === "number") {
      const num1 = +v1 || 0;
      const num2 = +v2 || 0;
      return num1 > num2 ? -sortDirection : sortDirection;
    }

    const p1 = `${v1}`.toLowerCase();
    const p2 = `${v2}`.toLowerCase();
    return p1 > p2 ? -sortDirection : sortDirection;
  };

  const doSort = (items: any[]) => (sort ? items.sort(sortFn) : items);

  const doFilter = (items: any[]) => {
    if (filter && filterOn) {
      return items.filter((item) => item[filter.filterBy]);
    }
    return items;
  };

  const doSearchWithExactMatchPriority = (items: any[]) => {
    if (search && searchTerm) {
      const cleanedSearchTerm = searchTerm.trim().toLowerCase();
      // the Boolean function is a way no how to filter out empty strings
      const searchWords = cleanedSearchTerm.split(" ").filter(Boolean);
      const searchTermResults = items.filter((item) =>
        search.some((key) => String(item[key]).toLowerCase().includes(cleanedSearchTerm)),
      );

      if (searchWords.length < 2) {
        return searchTermResults;
      }

      const searchWordsResults = items.filter((item) =>
        search.some((key) => searchWords.every((word) => String(item[key]).toLowerCase().includes(word))),
      );

      return [...searchTermResults, ...searchWordsResults.filter((x) => !searchTermResults.includes(x))];
    }
    return items;
  };

  const filteredItems = doSort(doSearchWithExactMatchPriority(doFilter(safeItems)));

  // Clamp page to valid bounds based on filtered items
  const maxPage = Math.max(0, Math.ceil(filteredItems.length / rowsPerPage) - 1);
  const validPage = Math.min(page, maxPage);

  const paginate = (items: any[]) =>
    pagination ? items.slice(validPage * rowsPerPage, validPage * rowsPerPage + rowsPerPage) : items;

  const drawItems = paginate(filteredItems);

  return (
    <>
      <TableContainer component={WrapperComponent || Paper} style={containerStyle} variant="outlined">
        {(search || filter || TopComponent) && (
          <>
            <Box
              className="input-wrapper"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                height: "3rem",
                alignItems: "center",
                p: "1rem 0.5rem",
              }}
            >
              {users && <h3 className="d-none user-title">{t("all_users")}</h3>}
              {search && (
                <SearchField
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newSearchTerm = e.target.value;
                    setSearch(newSearchTerm);
                    setPage(0);
                    setPreferences({ ...preferences, searchTerm: newSearchTerm, page: 0 });
                  }}
                />
              )}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {filter && (
                  <span style={{ minWidth: "240px" }}>
                    <StyledSwitch
                      title={filter.controlLabel}
                      checked={!filterOn}
                      onChange={() => {
                        setPage(0);
                        setPreferences({ ...preferences, filter: !filterOn, page: 0 });
                      }}
                    />
                    {filter.controlLabel}
                  </span>
                )}
                {TopComponent || null}
              </Box>
            </Box>
            <Divider />
          </>
        )}
        <Table size="small" className="user-table" style={tableStyle}>
          {header && header?.length > 0 && (
            <StyledTableHead>
              <TableRow className={users ? "custom-row" : undefined}>
                {header.map((item) => (
                  <TableCell key={item.title} style={item.style}>
                    {item.sort ? (
                      <TableSortLabel direction={sortDirection > 0 ? "asc" : "desc"} onClick={() => sortBy(item.sort!)}>
                        {" "}
                        {item.title}{" "}
                      </TableSortLabel>
                    ) : (
                      item.title
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </StyledTableHead>
          )}
          <TableBody>
            {drawItems.map((item, index) => (
              <Fragment key={item.id || item.label || index}>{renderChildren(item, index)}</Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredItems.length}
          rowsPerPage={rowsPerPage}
          page={validPage}
          labelRowsPerPage={t("pagination.rows_per_page")}
          labelDisplayedRows={({ from, to, count }) =>
            count !== -1
              ? t("pagination.displayed_rows", { from, to, count })
              : t("pagination.displayed_rows_more", { from, to })
          }
          getItemAriaLabel={(type) => t(`pagination.aria.${type}`)}
          onPageChange={(_, newPage) => {
            setPage(newPage);
            setPreferences({ ...preferences, page: newPage });
          }}
          onRowsPerPageChange={(e) => {
            const newPageSize = +e?.target?.value || DEFAULT_ROWS_PER_PAGE;
            setRowsPerPage(newPageSize);
            setPage(0);
            setPreferences({ ...preferences, page: 0, pageSize: newPageSize });
          }}
        />
      )}
    </>
  );
};

export default TableComponent;
